import { Slider, Stack, Typography } from '@mui/material';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  value: number[];
  loading: boolean;

  handleTextFieldValueChange(index: number): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleValueChange(event: Event, newValue: number | number[], activeThumb: number): void;

  handleResetYearsOfExperience(): void;

  handleUpdateYearsOfExperience(): void;
}

const ExperienceSlider: React.FC<Props> = ({
  value,
  loading,
  handleTextFieldValueChange,
  handleValueChange,
  handleResetYearsOfExperience,
  handleUpdateYearsOfExperience,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <Stack>
      <Typography className={classes.label}>{t('yearsOfExperience')}</Typography>
      <Stack className={classes.sliderContainer} gap={2}>
        <Stack direction={'row'} justifyContent={'center'} alignItems={'center'} padding={'0 8px'} gap={2}>
          <Typography className={classes.sliderLabel}>{t('from')}</Typography>
          <PaiTextField
            value={value[0]}
            type={'number'}
            min={0}
            max={30}
            variant={'background'}
            onChange={handleTextFieldValueChange(0)}
            disabled={loading}
          />
          <Typography className={classes.sliderLabel}>{t('upTo')}</Typography>
          <PaiTextField
            value={value[1]}
            type={'number'}
            min={0}
            max={30}
            variant={'background'}
            onChange={handleTextFieldValueChange(1)}
            disabled={loading}
          />
          <Typography className={classes.sliderLabel}>{t('years')}</Typography>
        </Stack>
        <Stack gap={1} justifyContent={'center'} alignItems={'center'}>
          <Slider className={classes.slider} value={value} step={1} max={30} min={0} onChange={handleValueChange} />
          <Stack className={classes.sliderLabels} direction={'row'} justifyContent={'space-between'} sx={{ width: '100%' }}>
            <Typography className={classes.sliderLabel}>{t('lessThanOne')}</Typography>
            <Typography className={classes.sliderLabel}>30+</Typography>
          </Stack>
          <Stack direction={'row'} justifyContent={'flex-end'} gap={2}>
            <PaiLink text={t('reset')} size={'md'} action={handleResetYearsOfExperience} disabled={loading} />
            <PaiButton text={t('update')} size={'small'} border={'rounded'} action={handleUpdateYearsOfExperience} disabled={loading} />
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  label: {
    marginBottom: '8px',
    marginLeft: '4px',
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
  },
  sliderContainer: {
    border: `1px solid ${theme.palette.greys.light}`,
    padding: '16px 8px',
    borderRadius: '8px',
  },
  sliderLabels: {
    width: 'calc(100% - 16px)',
  },
  slider: {
    height: '6px',
    width: 'calc(100% - 32px)',
    padding: '11px 0px',
    '& .MuiSlider-rail': {
      borderRadius: '6px',
      backgroundColor: theme.palette.backgrounds.main_new,
      opacity: 1,
    },
    '& .MuiSlider-track': {
      borderRadius: '6px',
      backgroundColor: theme.palette.primary.main,
      border: 'none',
    },
    '& .MuiSlider-thumb': {
      borderRadius: '4px',
      backgroundColor: theme.palette.primary.main,
      width: '20px',
      height: '20px',
    },
  },
  sliderLabel: {
    fontSize: theme.typography.pxToRem(13),
  },
}));

export default ExperienceSlider;
